/**
 * A thread: its root, its replies, and a composer that answers in it.
 *
 * The desktop's `ThreadPane`: a "Thread" head with "in #channel · 3 replies"
 * under it and a close button, the replies through the transcript's own rows,
 * and a composer whose placeholder is "Reply in thread". Beside the
 * conversation on a wide window, the whole pane on a narrow one.
 *
 * **The address is the state.** `/c/:id?thread=<root>` is what opens it, so a
 * reload restores it and Back closes it. Closing is navigating to `/c/:id`.
 * The reply count is the pane's own — what it holds — rather than the
 * summary's, which is the desktop's choice too: the number shown is the number
 * you can see.
 *
 * What it does when the root is not there is `threadPaneState`'s: a deleted
 * root closes the pane, as the desktop's does; one below what has loaded is
 * fetched page by page, which is how an address naming an old thread is
 * restored; and one that is nowhere says so.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ds } from '../../ds';
import { useI18n } from '../../i18n';
import { useRouter } from '../../router';
import { store, useStore } from '../../lib/relay/store';
import { threadPaneState } from '../../lib/relay/thread';
import { Empty, PanelHeader } from '../Shell';
import { Composer, type Reference } from './Composer';
import { mentionCandidates } from './mention';
import { ReadOnlyNotice } from './Official';
import { Transcript } from './Transcript';
import type { Conversation, Message, Person, RelayStore } from '../../lib/relay/types';
import './chat.css';

export function ThreadPane({
  conversation,
  rootId,
  people,
  docked
}: {
  conversation: Conversation;
  rootId: string;
  people: Person[];
  /** Beside the conversation, rather than in place of it. */
  docked: boolean;
}) {
  const { IconButton } = ds();
  const { t, tn } = useI18n();
  const { navigate } = useRouter();
  const id = conversation.id;

  const view = useStore(
    useCallback((s: RelayStore) => s.thread(id, rootId), [id, rootId])
  );
  const hasOlder = useStore(useCallback((s: RelayStore) => s.hasOlder(id), [id]));
  const landed = useStore(useCallback((s: RelayStore) => s.messages(id).length, [id]));
  const me = useStore((s) => s.me());
  const state = threadPaneState(view, hasOlder);
  const [reference, setReference] = useState<Reference | undefined>(undefined);

  const close = useCallback(
    (replace = false) => navigate(`/c/${id}`, { keepScroll: true, replace }),
    [navigate, id]
  );

  // The quote belongs to the thread it was made in.
  useEffect(() => setReference(undefined), [id, rootId]);

  useEffect(() => {
    // Replaced rather than pushed, so Back does not reopen a thread whose
    // root is gone.
    if (state === 'closed') close(true);
  }, [state, close]);

  // One page at a time until the root lands or the floor is reached. The
  // guard is cleared on any arrival as well as on the answer, for the reason
  // the transcript's is: a dropped socket never answers. A page that adds
  // nothing to the transcript stops the walk — below `joined_seq` the server
  // answers short for ever — until something does land, which is also what
  // scrolling the transcript up does.
  const loading = useRef(false);
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    loading.current = false;
    setStalled(false);
  }, [landed]);
  useEffect(() => {
    if (state !== 'loading' || stalled || loading.current) return;
    loading.current = true;
    void store.loadOlder(id).finally(() => {
      loading.current = false;
      if (store.messages(id).length === landed) setStalled(true);
    });
  }, [state, stalled, id, landed]);

  const place = conversation.kind === 'channel' ? `#${conversation.title}` : conversation.title;
  const nameOf = (message: Message) =>
    people.find((p) => p.id === message.senderId)?.name ?? '';

  return (
    <aside className={docked ? 'rl-thread rl-thread-docked' : 'rl-thread'}>
      <PanelHeader
        title={t('conversation.thread.title')}
        subtitle={
          view.root
            ? tn('conversation.thread.context', view.replies.length, { place })
            : undefined
        }
        actions={<IconButton icon="Close" size="sm" label={t('common.close')} onClick={() => close()} />}
      />

      {state === 'open' && view.root ? (
        <>
          <Transcript
            conversationId={id}
            messages={[view.root, ...view.replies]}
            people={people}
            onReply={(message) => setReference({ message, name: nameOf(message) })}
          />
          {conversation.official ? (
            <ReadOnlyNotice />
          ) : (
            <Composer
              key={rootId}
              conversationId={id}
              threadRoot={rootId}
              placeholder={t('conversation.thread.reply')}
              {...(reference ? { reference } : {})}
              onClearReference={() => setReference(undefined)}
              candidates={mentionCandidates(people, me?.id)}
            />
          )}
        </>
      ) : (
        <Empty
          title={
            state === 'missing' || stalled ? t('conversation.thread.missing') : t('app.loading')
          }
        />
      )}
    </aside>
  );
}
